import { Component, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import {
  NotificationPreferences,
  NotificationPreferenceType
} from '../../models/notification-preferences.model';
import {
  notificationTypeEnabled,
  shouldShowNotificationType
} from '../../utils/notification-preferences-form.utils';
import { notificationPreferencesCompanyName } from '../../utils/notification-preferences.helpers';

export type NotificationPreferencesFormLayout = 'callback' | 'embedded';

/**
 * Shared email notification toggles. Use `callback` inside the Auth0 callback overlay;
 * use `embedded` on full app pages (e.g. Settings).
 */
@Component({
  selector: 'lr-notification-preferences-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatButtonModule, MatCardModule, MatSlideToggleModule],
  templateUrl: './notification-preferences-form.component.html',
  styleUrls: ['./notification-preferences-form.component.scss'],
  host: {
    '[class.npf-callback-host]': "layout() === 'callback'"
  }
})
export class NotificationPreferencesFormComponent {
  layout = input<NotificationPreferencesFormLayout>('embedded');
  form = input.required<FormGroup>();
  preferences = input.required<NotificationPreferences>();
  isSaving = input(false);
  isLoading = input(false);
  /** Settings: false while the form matches saved preferences. First-login overlay omits this. */
  canSave = input(true);
  errorMessage = input<string | null>(null);
  /** Callback layout: company label (falls back to preferences.masterClientName). */
  companyName = input<string | null>(null);
  /** Callback layout: show guidance for managing other companies via Settings. */
  hasMultipleCompanies = input(false);
  /** Callback overlay only: second action to continue without saving. */
  showSkip = input(false);

  save = output<void>();
  skip = output<void>();

  showSection(type: NotificationPreferenceType): boolean {
    return shouldShowNotificationType(notificationTypeEnabled(this.preferences(), type));
  }

  hasAnySection(): boolean {
    return (
      this.showSection('billing') ||
      this.showSection('enrollment') ||
      this.showSection('cancellation')
    );
  }

  displayCompanyName(): string | null {
    return notificationPreferencesCompanyName(this.preferences(), this.companyName());
  }

  saveDisabled(): boolean {
    return this.isSaving() || this.isLoading() || !this.hasAnySection() || !this.canSave();
  }
}
